import { Button, Flex, Typography } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Chat } from '../../components/chat/chat'
import { useAssistantMessage } from '../../prompts/hooks'
import { useAuth } from '../../auth'
import { StopAndRateConversationResult } from '../../prompts/output/stop-and-rate-conversation-result'
import { Campaign } from '../campaign'
import { CandidateAttributes } from '../../candidate/components/candidate-attributes'
import { Message } from '../../candidate/message'
import { ToolTestControls } from '../../app-connection/components/tool-test-controls'
import { ToolActivity, ToolTestMode } from '../../app-connection/types'
import { AssistantMessageInput } from '../../prompts/input/assistant-message-input'

export type CampaignTryOutContentProps = {
    campaign: Campaign
    onShowScore?: (match: StopAndRateConversationResult) => void
    onTryAgain?: () => void
    showScoreInline?: boolean
}

type TryOutMessage = Omit<Message, 'updatedAt' | 'mediaUrl' | 'id'>

type ConversationInput = Omit<AssistantMessageInput, 'type' | 'data' | 'tools' | 'title' | 'locale'>

function toAssistantMessageInput(campaign: Campaign, conversation: ConversationInput): AssistantMessageInput {
    const base = {
        ...conversation,
        campaignId: campaign.id,
        title: campaign.title,
        startMessageTemplateId: campaign.startMessageTemplateId,
        locale: campaign.locale,
        allowLanguageSwitch: campaign.allowLanguageSwitch,
    }

    switch (campaign.type) {
        case 'interview':
            return {
                ...base,
                type: campaign.type,
                data: campaign.data,
            }
        case 'custom':
            return {
                ...base,
                type: campaign.type,
                data: campaign.data,
                tools: campaign.tools,
            }
        case 'sales':
            return {
                ...base,
                type: campaign.type,
                data: campaign.data,
            }
    }
}

export function CampaignTryOutContent({
    campaign,
    onShowScore,
    onTryAgain,
    showScoreInline = false,
}: CampaignTryOutContentProps) {
    const { t } = useTranslation()
    const { user } = useAuth()

    const [messages, setMessages] = useState<TryOutMessage[]>([])
    const [conversationLanguage, setConversationLanguage] = useState<string | null>()
    const [toolTestMode, setToolTestMode] = useState<ToolTestMode>('safe')
    const [toolActivities, setToolActivities] = useState<ToolActivity[]>([])
    const [match, setMatch] = useState<StopAndRateConversationResult>()
    const [showScore, setShowScore] = useState(false)
    const revision = useRef(0)
    const started = useRef(false)

    const assistantMessageMutation = useAssistantMessage()

    const sendAssistantMessage = (content?: string, restart = false) => {
        const requestRevision = revision.current
        const isStillCurrent = () => requestRevision === revision.current
        let totalMessages = restart ? [] : messages

        if (content) {
            totalMessages = [
                ...totalMessages,
                {
                    role: 'user',
                    content,
                    type: 'text',
                    createdAt: new Date(),
                },
            ]
            setMessages(totalMessages)
        }

        const input = toAssistantMessageInput(campaign, {
            toolTestMode,
            toolTurnId: crypto.randomUUID(),
            messages: totalMessages,
            name: user!.name,
            conversationLanguage: restart ? null : conversationLanguage,
        })

        return assistantMessageMutation.mutateAsync(input).then((result) => {
            if (!isStillCurrent()) {
                return
            }

            setToolActivities((previous) => [...previous, ...result.toolActivities])
            setConversationLanguage(result.conversationLanguage)
            setMessages((messages) => [
                ...messages,
                {
                    role: 'assistant',
                    content: result.message,
                    type: 'text',
                    createdAt: new Date(),
                },
            ])

            if ('matchScore' in result) {
                setMatch(result)
            }
        })
    }

    // A new revision makes answers to the previous conversation that are still underway ignored.
    const resetConversation = () => {
        revision.current++
        setToolActivities([])
        setMessages([])
        setConversationLanguage(undefined)
        setMatch(undefined)
        setShowScore(false)
    }

    const startTryOut = () => {
        resetConversation()
        void sendAssistantMessage(undefined, true)
    }

    // The conversation starts again in the new mode, from the effect below.
    const changeToolTestMode = (mode: ToolTestMode) => {
        resetConversation()
        setToolTestMode(mode)
        started.current = false
    }

    const handleViewScore = () => {
        if (showScoreInline) {
            setShowScore(true)
        } else if (onShowScore && match) {
            onShowScore(match)
        }
    }

    const handleTryAgain = () => {
        setShowScore(false)
        startTryOut()
        onTryAgain?.()
    }

    // Runs after every render; the ref makes sure the conversation starts only once.
    useEffect(() => {
        if (!started.current && user) {
            started.current = true
            void sendAssistantMessage(undefined, true)
        }
    })

    const isSendingMessage = assistantMessageMutation.isPending
    const hasTools = campaign.type === 'custom' && campaign.tools.length > 0

    if (showScoreInline && showScore && match) {
        return (
            <Flex
                vertical
                gap="large"
                className="text-center"
            >
                <div>
                    <Typography.Title>{(match.matchScore || 0) >= 70 ? '🏆' : '😐'}</Typography.Title>
                    <Typography.Title
                        level={2}
                        className="m-0"
                    >
                        {match.matchScore}%
                    </Typography.Title>
                </div>
                <CandidateAttributes
                    positive={match.positiveCandidateAttributes}
                    negative={match.negativeCandidateAttributes}
                />
                <Flex justify="center">
                    <Button
                        size="large"
                        type="primary"
                        onClick={handleTryAgain}
                    >
                        {t('Try again')}
                    </Button>
                </Flex>
            </Flex>
        )
    }

    return (
        <Flex
            vertical
            gap="middle"
        >
            {hasTools && (
                <ToolTestControls
                    mode={toolTestMode}
                    onChange={changeToolTestMode}
                    activities={toolActivities}
                    disabled={isSendingMessage}
                />
            )}
            <Chat
                disabled={isSendingMessage}
                messages={messages}
                onMessageAdded={match ? undefined : sendAssistantMessage}
                height={400}
            />
            {match && (
                <Flex
                    justify="end"
                    className="w-full"
                >
                    <Button
                        size="large"
                        type="primary"
                        onClick={handleViewScore}
                    >
                        {t('View score')}
                    </Button>
                </Flex>
            )}
        </Flex>
    )
}
